import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createEvent,
    deleteEvent,
    fetchEvent,
    fetchEvents,
    fetchEventSignups,
    previewEventEmail,
    updateEvent,
    type CreateEventInput,
    type EventsListParams,
    type EventEmailPreviewInput,
    type EventSignupsParams,
    type UpdateEventInput
} from '../services/events';
import {
    EVENT_DETAIL_KEY,
    EVENT_EMAIL_PREVIEW_KEY,
    EVENT_SIGNUPS_KEY,
    EVENTS_KEY
} from './queryKeys';

/** The admin event catalog is small - one paged fetch backs the whole list,
 *  and `EventsList` searches/sorts/paginates it client-side. */
export function useEvents(params: EventsListParams) {
    const { data, isLoading, isFetching, isError } = useQuery({
        queryKey: [EVENTS_KEY, 'list', params.page, params.limit],
        queryFn: () => fetchEvents(params),
        staleTime: 30_000
    });

    return {
        events: data?.data ?? [],
        meta: data?.meta ?? { total: 0, page: params.page, limit: params.limit, totalPages: 1 },
        isLoading,
        isFetching,
        isError
    };
}

/** Backs the attendee panel - paginated the same way `usePracticeUsers` is. */
export function useEventSignups(eventId: string | undefined, params: EventSignupsParams) {
    const { page, limit } = params;
    const query = useQuery({
        queryKey: EVENT_SIGNUPS_KEY(eventId ?? '', page, limit),
        queryFn: () => fetchEventSignups(eventId as string, { page, limit }),
        enabled: Boolean(eventId),
        staleTime: 30_000,
        placeholderData: keepPreviousData
    });

    return {
        signups: query.data?.data ?? [],
        meta: query.data?.meta ?? { total: 0, page, limit, totalPages: 1 },
        isLoading: query.isLoading,
        isError: query.isError
    };
}

/** Renders the confirmation email for the form's current (debounced) draft -
 *  `placeholderData` keeps the last render on screen instead of flashing a spinner. */
export function useEventEmailPreview(input: EventEmailPreviewInput | null) {
    const fingerprint = input ? JSON.stringify(input) : '';
    const query = useQuery({
        queryKey: EVENT_EMAIL_PREVIEW_KEY(fingerprint),
        queryFn: () => previewEventEmail(input as EventEmailPreviewInput),
        enabled: Boolean(input),
        placeholderData: (previous) => previous,
        staleTime: 5 * 60_000
    });

    return { preview: query.data ?? null, isLoading: query.isLoading, isError: query.isError };
}

/** Backs the edit drawer - fetched fresh per open so audit fields are current. */
export function useEvent(id: string | undefined) {
    const query = useQuery({
        queryKey: EVENT_DETAIL_KEY(id ?? ''),
        queryFn: () => fetchEvent(id as string),
        enabled: Boolean(id),
        staleTime: 30_000
    });

    return {
        event: query.data ?? null,
        isLoading: query.isLoading,
        isError: query.isError
    };
}

export function useCreateEvent() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateEventInput) => createEvent(input),
        onSuccess: (created) => {
            queryClient.setQueryData(EVENT_DETAIL_KEY(created.id), created);
            queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] });
        }
    });
}

export function useUpdateEvent() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: UpdateEventInput }) =>
            updateEvent(id, patch),
        onSuccess: (updated) => {
            queryClient.setQueryData(EVENT_DETAIL_KEY(updated.id), updated);
            queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] });
        }
    });
}

export function useDeleteEvent() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteEvent(id),
        onSuccess: (_data, id) => {
            queryClient.removeQueries({ queryKey: EVENT_DETAIL_KEY(id) });
            queryClient.invalidateQueries({ queryKey: [EVENTS_KEY] });
        }
    });
}
